
// ======================================
// Customer Profile Page
// ======================================

import { authAPI } from '../../lib/api.js';
import { getUser, logout } from '../../lib/auth.js';
import { icons } from '../../components.js';

export function renderProfilePage() {
    const user = getUser();

    // If no user, redirect to login handled by Auth Guard or API error logic
    if (!user) {
        window.location.href = '/login';
        return '';
    }

    // Get initials for avatar
    const initials = user.name ? user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U';

    return `
    <div class="container section">
      <div class="profile-layout">
        <aside class="profile-sidebar">
          <div class="profile-card">
            <div class="profile-avatar-large">
              ${initials}
            </div>
            <h2 class="profile-name">${user.name}</h2>
            <p class="profile-email">${user.email}</p>
            <div class="profile-role-badge">${user.role.toUpperCase()}</div>
          </div>
          
          <nav class="profile-nav">
            <a href="/profile" class="profile-nav-link active">
              ${icons.users} My Profile
            </a>
            <a href="/my-projects" class="profile-nav-link">
              ${icons.home} My Projects
            </a>
            <a href="/my-documents" class="profile-nav-link">
              ${icons.tool} Documents
            </a>
            <button id="logout-btn-profile" class="profile-nav-link logout-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
              Logout
            </button>
          </nav>
        </aside>

        <main class="profile-content">
          <div class="content-card">
            <div class="card-header">
              <h3>Personal Information</h3>
              <p class="text-muted">Update your personal details here.</p>
            </div>
            
            <form id="profile-form" class="profile-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="name" class="form-label">Full Name</label>
                  <input type="text" id="name" name="name" class="form-input" value="${user.name}" required>
                </div>
                <div class="form-group">
                  <label for="phone" class="form-label">Phone Number</label>
                  <input type="tel" id="phone" name="phone" class="form-input" value="${user.phone || ''}" required>
                </div>
              </div>

              <div class="form-group">
                <label for="email" class="form-label">Email Address</label>
                <input type="email" id="email" class="form-input" value="${user.email}" disabled style="background-color: var(--color-bg-alt); cursor: not-allowed;">
                <small class="text-muted">Email cannot be changed directly. Contact support for assistance.</small>
              </div>

              <div class="form-group">
                <label for="address" class="form-label">Address</label>
                <textarea id="address" name="address" class="form-input" rows="3">${user.address || ''}</textarea>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="city" class="form-label">City</label>
                  <input type="text" id="city" name="city" class="form-input" value="${user.city || ''}">
                </div>
                <div class="form-group">
                  <label for="pincode" class="form-label">Pincode</label>
                  <input type="text" id="pincode" name="pincode" class="form-input" value="${user.pincode || ''}">
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" class="btn btn-primary" id="save-profile-btn">Save Changes</button>
              </div>
            </form>
          </div>

          <div class="content-card mt-8">
            <div class="card-header">
              <h3>Change Password</h3>
              <p class="text-muted">Ensure your account is secure with a strong password.</p>
            </div>
            
            <form id="password-form" class="profile-form">
              <div class="form-group">
                <label for="current-password" class="form-label">Current Password</label>
                <input type="password" id="current-password" name="current_password" class="form-input" required>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label for="new-password" class="form-label">New Password</label>
                  <input type="password" id="new-password" name="new_password" class="form-input" required minlength="6">
                </div>
                <div class="form-group">
                  <label for="confirm-password" class="form-label">Confirm New Password</label>
                  <input type="password" id="confirm-password" name="confirm_password" class="form-input" required minlength="6">
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" class="btn btn-outline" id="change-pwd-btn">Update Password</button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>

    <style>
      .profile-layout {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: var(--space-8);
        align-items: start;
      }

      .profile-sidebar {
        position: sticky;
        top: 100px;
      }

      .profile-card {
        background: var(--color-bg);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        padding: var(--space-6);
        text-align: center;
        margin-bottom: var(--space-4);
      }

      .profile-avatar-large {
        width: 80px;
        height: 80px;
        background: var(--color-primary);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-2xl);
        font-weight: 600;
        margin: 0 auto var(--space-4);
      }

      .profile-name {
        font-size: var(--text-lg);
        font-weight: 600;
        margin-bottom: var(--space-1);
      }

      .profile-email {
        color: var(--color-text-muted);
        font-size: var(--text-sm);
        margin-bottom: var(--space-3);
      }

      .profile-role-badge {
        display: inline-block;
        padding: 2px 8px;
        background: var(--color-bg-alt);
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        font-weight: 500;
        color: var(--color-text-muted);
      }

      .profile-nav {
        background: var(--color-bg);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }

      .profile-nav-link {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        color: var(--color-text);
        text-decoration: none;
        transition: background 0.2s;
        border-bottom: 1px solid var(--color-border);
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        cursor: pointer;
        font-size: var(--text-base);
      }

      .profile-nav-link:last-child {
        border-bottom: none;
      }

      .profile-nav-link:hover {
        background: var(--color-bg-alt);
      }

      .profile-nav-link.active {
        background: var(--color-primary-light);
        color: var(--color-primary);
        font-weight: 500;
      }

      .profile-nav-link.logout-link {
        color: var(--color-error);
      }
      
      .profile-nav-link.logout-link:hover {
        background: #fee2e2;
      }

      .content-card {
        background: var(--color-bg);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        padding: var(--space-6);
      }

      .card-header {
        margin-bottom: var(--space-6);
        padding-bottom: var(--space-4);
        border-bottom: 1px solid var(--color-border);
      }

      .card-header h3 {
        font-size: var(--text-xl);
        font-weight: 600;
        margin-bottom: var(--space-1);
      }

      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-4);
      }

      .mt-8 {
        margin-top: var(--space-8);
      }

      @media (max-width: 768px) {
        .profile-layout {
          grid-template-columns: 1fr;
        }
        
        .profile-sidebar {
          position: static;
        }

        .form-row {
          grid-template-columns: 1fr;
        }
      }
    </style>
  `;
}

export function initProfilePage() {
    const profileForm = document.getElementById('profile-form');
    const passwordForm = document.getElementById('password-form');
    const logoutBtn = document.getElementById('logout-btn-profile');

    // Handle Logout
    if (logoutBtn) {
        logoutBtn.addEventListener('click', () => {
            logout();
        });
    }

    // Handle Profile Update
    if (profileForm) {
        profileForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            const btn = document.getElementById('save-profile-btn');
            const originalText = btn.textContent;

            const formData = {
                name: document.getElementById('name').value,
                phone: document.getElementById('phone').value,
                address: document.getElementById('address').value,
                city: document.getElementById('city').value,
                pincode: document.getElementById('pincode').value
            };

            try {
                btn.disabled = true;
                btn.textContent = 'Saving...';

                await authAPI.updateProfile(formData);

                // Update local storage user data
                const currentUser = getUser();
                const updatedUser = { ...currentUser, ...formData };
                localStorage.setItem('nvi_user', JSON.stringify(updatedUser));

                // Update header user name immediately
                const headerName = document.querySelector('.user-name');
                if (headerName) headerName.textContent = formData.name;

                alert('Profile updated successfully!');
            } catch (error) {
                console.error('Update failed:', error);
                alert(error.message || 'Failed to update profile');
            } finally {
                btn.disabled = false;
                btn.textContent = originalText;
            }
        });
    }

    // Handle Password Change
    if (passwordForm) {
        passwordForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            const btn = document.getElementById('change-pwd-btn');
            const originalText = btn.textContent;

            const currentPassword = document.getElementById('current-password').value;
            const newPassword = document.getElementById('new-password').value;
            const confirmPassword = document.getElementById('confirm-password').value;

            if (newPassword !== confirmPassword) {
                alert('New passwords do not match');
                return;
            }

            try {
                btn.disabled = true;
                btn.textContent = 'Updating...';

                await authAPI.changePassword({
                    current_password: currentPassword,
                    new_password: newPassword
                });

                alert('Password changed successfully!');
                passwordForm.reset();
            } catch (error) {
                console.error('Password change failed:', error);
                alert(error.message || 'Failed to change password');
            } finally {
                btn.disabled = false;
                btn.textContent = originalText;
            }
        });
    }
}
